import { Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import {
  DatasetSortEnum,
  DatasetStatus,
  EnrichingDS,
  InventoryFilterData,
} from "types";

import axios from "helpers/axiosInstance";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";

import { State } from "redux/store";
import {
  CLEAR_EXPORT_DATA,
  ERROR_TEXT,
  IS_CONTENT_LOADING,
  IS_ERROR,
  IS_LOADING,
  LIMIT_ERROR,
  SET_DATASETS,
  SET_DATASET_ID,
  SET_DATASET_PROGRESS,
  SET_HEALTH_STATUS,
  SET_SELECTED_SAMPLE_DATASET,
  SET_DELETE_MODAL_ID,
  SET_SELECTED_EXPORT_IDS,
  SET_SELECTED_IMAGE_COUNT,
  SET_DUPLICATE_DATASET,
  SET_ENRICHING_DS,
  SET_FILTER_DATA,
} from "./constants";
import { AxiosProgressEvent } from "axios";
import { getDatasets, getEnrichingDataset } from "./selectors";
import {
  resetStore,
  abortEnrichment,
  fetchEnrichmentContext,
} from "redux/Enrichment/actions";
import { setShowInternalErrorModal } from "redux/Modals/actions";
import { isOnPremUser } from "../../helpers/utility/utilities";
import { getUserInfo } from "../User/selectors";

export const setContentLoader = (data: any) => {
  return {
    type: IS_CONTENT_LOADING,
    data,
  };
};

export const setLoader = (data: any) => {
  return {
    type: IS_LOADING,
    data,
  };
};

export const setSelectedSampleDataset = (data: any) => {
  return {
    type: SET_SELECTED_SAMPLE_DATASET,
    data,
  };
};

export const setDatasetProgress = (datasetId: any, progress: any) => {
  return {
    type: SET_DATASET_PROGRESS,
    datasetId,
    progress,
  };
};

export const setDatasets = (data: any) => {
  return {
    type: SET_DATASETS,
    data,
  };
};

export const setDatasetId = (data: any) => {
  return {
    type: SET_DATASET_ID,
    data,
  };
};

export const setIsError = (data: any) => {
  return {
    type: IS_ERROR,
    data,
  };
};

export const setErrorText = (data: any) => {
  return {
    type: ERROR_TEXT,
    data,
  };
};

export const setLimitError = (data: string | null) => {
  return { type: LIMIT_ERROR, data };
};

export const setDeleteModalId = (data: any) => {
  return {
    type: SET_DELETE_MODAL_ID,
    data,
  };
};

export const setHealthStatus = (data: any) => {
  return {
    type: SET_HEALTH_STATUS,
    data,
  };
};

export const clearExportData = () => {
  return { type: CLEAR_EXPORT_DATA };
};

export const setDatasetIdToDuplicate = (data: any) => {
  return {
    type: SET_DUPLICATE_DATASET,
    data,
  };
};

export const setEnrichingDataset = (data: null | EnrichingDS) => {
  return { type: SET_ENRICHING_DS, data };
};

export const setFilterData = (data: InventoryFilterData) => {
  return { type: SET_FILTER_DATA, data };
};

export const setSelectedExportIDs = (
  imageID: string | null,
  totalData: { totalIDs: { string: [] }; totalCount: number } | null,
  objectID: string | null,
): ThunkAction<void, State, unknown, any> => {
  return (dispatch: Dispatch, getState: () => State) => {
    //handle select all action
    if (totalData?.totalIDs && totalData.totalCount) {
      dispatch({ type: SET_SELECTED_EXPORT_IDS, data: totalData.totalIDs });
      dispatch({ type: SET_SELECTED_IMAGE_COUNT, data: totalData.totalCount });
      return;
    }

    if (imageID) {
      const currentExportIDs = { ...getState().datasets.selectedExportIDs };
      const imageIDExists = Object.keys(currentExportIDs).includes(imageID);

      const selectedObjectIDs = currentExportIDs[imageID] || [];

      const objectIDExists = selectedObjectIDs.includes(objectID);
      const isLastObjectID = objectIDExists && selectedObjectIDs.length === 1;

      // if imageExists and hasonly on objectID in array and it is selected again
      if (imageIDExists && isLastObjectID && objectID) {
        delete currentExportIDs[imageID];
        dispatch({ type: SET_SELECTED_EXPORT_IDS, data: currentExportIDs });
      } else {
        // if neither imageExists nor objectExists
        if (!imageIDExists && !objectIDExists) {
          let newObjectIDArray = [...selectedObjectIDs, objectID];
          let newExportIDs = {
            ...currentExportIDs,
            [imageID]: newObjectIDArray,
          };
          dispatch({ type: SET_SELECTED_EXPORT_IDS, data: newExportIDs });
        }
        // if imageExists but does not have current objectID
        else if (imageIDExists && !objectIDExists && objectID) {
          let newObjectIDs = [...currentExportIDs[imageID], objectID];
          let newExportIDs = {
            ...currentExportIDs,
            [imageID]: newObjectIDs,
          };
          dispatch({ type: SET_SELECTED_EXPORT_IDS, data: newExportIDs });
        }
        // if imageExists and has current objectID in array
        else if (imageIDExists && objectIDExists && objectID) {
          let newObjectIDs = selectedObjectIDs.filter(
            (id: any) => id !== objectID,
          );
          let newExportIDs = {
            ...currentExportIDs,
            [imageID]: newObjectIDs,
          };
          dispatch({ type: SET_SELECTED_EXPORT_IDS, data: newExportIDs });
        }
        // if imageExists and there is no object ID passed
        else if (!objectID && imageIDExists) {
          delete currentExportIDs[imageID];
          dispatch({ type: SET_SELECTED_EXPORT_IDS, data: currentExportIDs });
        }
      }
    }
  };
};

export const fetchDatasets = (
  initialFetching = false,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    if (initialFetching) {
      dispatch(setDatasets([]));
      dispatch(setContentLoader(true));
    }
    try {
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        PRIVATE_DATASETS_PATH,
      );
      const response = await axios.get(url);
      dispatch(
        setDatasets(
          response.data.filter(
            (dataset: any) =>
              dataset.similarity_data || dataset.status !== "READY", // filter out old datasets
          ),
        ),
      );
      dispatch(setContentLoader(false));
    } catch (error) {
      dispatch(setContentLoader(false));
      console.log(error);
    }
  };
};

export const checkBucketList = (
  bucketName?: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/bucket?bucket_path=${bucketName}`,
      );
      const response = await axios.get(url);
      if (response.status === 200) {
        dispatch(setErrorText("success"));
      }
    } catch (error: any) {
      dispatch(setErrorText(error.response.data.detail));
    }
  };
};

export const deleteDataset = (
  datasetID: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      dispatch(setContentLoader(true));
      dispatch(setLoader(true));

      const datasets: {
        id: string;
        display_name: string;
        status: DatasetStatus;
      }[] = getDatasets(getState());
      const enrichingDataset = getEnrichingDataset(getState());

      const isEnrichingDataset = datasets?.some((dataset) => {
        if (dataset.id === datasetID) {
          return (
            dataset.display_name === enrichingDataset?.name &&
            dataset.status === DatasetStatus.READ_ONLY
          );
        }
        return false;
      });

      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetID}`,
      );
      const response = await axios.delete(url);
      if (response.status === 200) {
        dispatch(setErrorText(""));

        // If this is the dataset currently being enriched, abort enrichment
        if (isEnrichingDataset) {
          dispatch(setEnrichingDataset(null));
          await dispatch(fetchEnrichmentContext(datasetID) as any);
          await dispatch(abortEnrichment(datasetID) as any);
          dispatch(resetStore()); //reset enrichment states
        }
      }
    } catch (error: any) {
      dispatch(setErrorText(error.response.data.detail));
    }
  };
};

export const createDataset = (
  datasetName: any,
  s3Bucket?: any,
  selectedSampleId?: any,
  file?: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoader(true));
    dispatch(setDatasetId(null));
    try {
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        "/dataset",
      );
      var bodyFormData = new FormData();
      datasetName && bodyFormData.append("dataset_name", datasetName);
      s3Bucket && bodyFormData.append("bucket_path", s3Bucket);
      selectedSampleId &&
        bodyFormData.append("vl_dataset_id", selectedSampleId);
      file && bodyFormData.append("uploaded_filename", file.name);

      const response = await axios.post(url, bodyFormData);

      dispatch(setDatasetId(response.data.id));

      if (file) {
        dispatch(uploadDataset(response.data.id, file) as any);
      }
      dispatch(setLoader(false));
    } catch (error: any) {
      const errorResponse: { status: number; data: { detail: string } } =
        error.response;
      if (errorResponse.status === 412) {
        dispatch(setLimitError(errorResponse.data.detail));
      }
      if (errorResponse.status === 500) {
        dispatch(setShowInternalErrorModal(true));
      }
      dispatch(setLoader(false));
      dispatch(setIsError(true));
    }
  };
};

export const duplicateDataset = (
  datasetId: any,
  datasetName: any,
  pipelineType: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoader(true));
    try {
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetId}/duplicate`,
      );
      const bodyFormData = new FormData();
      bodyFormData.append("dataset_name", datasetName);
      bodyFormData.append("pipeline_type", pipelineType);

      await axios.post(url, bodyFormData);

      dispatch(setDatasetIdToDuplicate(null));
      dispatch(setLoader(false));
    } catch (error: any) {
      dispatch(setLoader(false));
    }
  };
};

export const uploadDataset = (
  datasetId: any,
  file: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoader(true));
    try {
      const url =
        process.env.REACT_APP_API_ENDPOINT +
        "/api/v1/dataset/" +
        datasetId +
        "/upload";

      const config = {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          dispatch(setDatasetProgress(datasetId, percentCompleted));
        },
      };

      var bodyFormData = new FormData();
      bodyFormData.append("file", file);
      await axios.post(url, bodyFormData, config);
      dispatch(setLoader(false));
    } catch (error: any) {
      dispatch(setLoader(false));
    }
  };
};

const updateEnrichmentProgressDS = (
  allDatasets: any[],
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const currentDS = getEnrichingDataset(state);

    // Find datasets that are actively enriching (hidden + INDEXING + has source)
    const enrichingDatasets = allDatasets.filter(
      (d: any) => d.hidden && d.status === "INDEXING" && d.source_dataset_id,
    );

    // Filter out invalid enrichments: source dataset must exist and be visible
    const validEnrichingDatasets = enrichingDatasets.filter((d: any) => {
      const sourceDataset = allDatasets.find(
        (source) => source.id === d.source_dataset_id,
      );
      return sourceDataset && !sourceDataset.hidden;
    });

    if (currentDS) {
      const currentDSInProgress = validEnrichingDatasets.find(
        (dataset) => dataset.id === currentDS.id,
      );

      if (currentDSInProgress) {
        // Still in progress - update progress if changed
        if (currentDSInProgress.progress > currentDS.progress) {
          dispatch(
            setEnrichingDataset({
              ...currentDS,
              progress: currentDSInProgress.progress,
              type: "IN_PROGRESS",
            }),
          );
        }
      } else {
        // Check if enrichment completed successfully
        const completedDataset = allDatasets.find(
          (d: any) =>
            d.id === currentDS.id && !d.hidden && d.status === "READY",
        );

        if (completedDataset) {
          dispatch(
            setEnrichingDataset({
              ...currentDS,
              progress: 100,
              type: "SUCCESS",
            }),
          );
        } else {
          // Enrichment is no longer valid - clear it
          dispatch(setEnrichingDataset(null));
        }
      }
    } else {
      // No current enriching dataset - start tracking if any exist
      if (validEnrichingDatasets.length) {
        const ds = validEnrichingDatasets[0];
        dispatch(
          setEnrichingDataset({
            id: ds.id,
            progress: ds.progress,
            name: ds.display_name,
            type: "IN_PROGRESS",
          }),
        );
      }
    }
  };
};

export const fetchSortedDatasets = (
  initialDatasetsFetch = false,
  sortValue: DatasetSortEnum = DatasetSortEnum.STATUS,
  hidden = true,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const params = {
      sort_by: sortValue,
      include_hidden: hidden ? "true" : "false",
    };
    const urlParams = new URLSearchParams(params);
    try {
      if (initialDatasetsFetch) {
        dispatch(setDatasets([]));
        dispatch(setLoader(true));
        dispatch(setContentLoader(true));
      }
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/datasets?${urlParams}`,
      );
      const response = await axios.get(url);

      dispatch(setLoader(false));
      dispatch(setContentLoader(false));
      datasetsEtag = response.headers["x-etag"] || null;
      const allDatasets = response.data;
      const visibleDatasets = allDatasets.filter(
        (dataset: any) => !dataset.hidden,
      );
      dispatch(setDatasets(visibleDatasets));
      dispatch(updateEnrichmentProgressDS(allDatasets) as any);
    } catch (error) {
      dispatch(setContentLoader(false));
      dispatch(setLoader(false));
      console.log(error);
    }
  };
};

let datasetsEtag: string | null = null;

export const fetchSortedDatasetsLongPoll = (
  isPrivate = false,
  sortValue: DatasetSortEnum = DatasetSortEnum.STATUS,
  hidden = true,
  signal?: AbortSignal,
): ThunkAction<Promise<void>, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const endpoint = isPrivate ? "datasets" : "pvld";
    const params: Record<string, string> = {
      sort_by: sortValue,
      include_hidden: hidden ? "true" : "false",
      wait: "true",
    };
    if (datasetsEtag) {
      params.etag = datasetsEtag;
    }
    const urlParams = new URLSearchParams(params);
    const url = composeApiURL(
      process.env.REACT_APP_API_ENDPOINT!,
      `/${endpoint}?${urlParams}`,
    );
    const response = await axios.get(url, {
      validateStatus: (s) => s === 200 || s === 304,
      signal,
    });

    if (response.status === 304) {
      return;
    }

    datasetsEtag = response.headers["x-etag"] || null;
    const allDatasets = response.data;
    const visibleDatasets = allDatasets.filter(
      (dataset: any) => !dataset.hidden,
    );
    dispatch(setDatasets(visibleDatasets));
    dispatch(updateEnrichmentProgressDS(allDatasets) as any);
  };
};

export const fetchHealthStatus = (): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    try {
      const state = getState();
      const userInfo = getUserInfo(state);
      const previousVersion = localStorage.getItem("vlVersion") || null;
      const prevHealtStatus = state.datasets.healthStatus;

      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/healthcheck`,
      );
      const response = await axios.get(url);
      const newHealthStatus = response.data.status;
      const newVersion = response.data.version || null;

      // trying to remove the noise of the health status in the redux dev tools so only updates when the status changes
      if (prevHealtStatus?.status !== newHealthStatus) {
        dispatch(setHealthStatus(response.data));
      }

      if (
        (userInfo.isInternalUser ||
          userInfo.identityProvider === "system" ||
          isOnPremUser()) &&
        newVersion
      ) {
        if (previousVersion !== newVersion) {
          localStorage.setItem("vlVersion", newVersion);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };
};
